import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Truck, Calendar, Clock, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BuchungBestaetigung() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading, success, failed, error
  const [booking, setBooking] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollPaymentStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollPaymentStatus = async (sid) => {
    const maxAttempts = 8;
    const pollInterval = 2500;

    if (pollCount.current >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/bookings/status/${sid}`);
      const data = await res.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setBooking(data.booking);
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Still pending - poll again
      pollCount.current += 1;
      setTimeout(() => pollPaymentStatus(sid), pollInterval);
    } catch {
      setStatus('error');
    }
  };

  return (
    <main data-testid="buchung-bestaetigung-page" className="pt-20 min-h-screen bg-[#0F1A2E]">
      <section className="section-spacing">
        <div className="container-main max-w-2xl mx-auto">
          {status === 'loading' && <LoadingState />}
          {status === 'success' && booking && <SuccessState booking={booking} />}
          {status === 'failed' && <FailedState />}
          {status === 'error' && <ErrorState />}
        </div>
      </section>
    </main>
  );
}

function LoadingState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A1220] p-8 md:p-12 border border-[#1e3a5f] text-center"
    >
      <Loader2 size={48} className="text-[#0F1A2E] animate-spin mx-auto mb-6" />
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
        Zahlung wird verarbeitet...
      </h2>
      <p className="text-white/70">
        Bitte warten Sie einen Moment. Ihre Zahlung wird überprüft.
      </p>
    </motion.div>
  );
}

function SuccessState({ booking }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-[#0A1220] p-8 md:p-12 border border-[#1e3a5f]"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-3">
          Buchung bestätigt!
        </h2>
        <p className="text-white/70">
          Vielen Dank für Ihre Buchung. Hier sind Ihre Buchungsdetails.
        </p>
      </div>

      <div className="space-y-4 mb-8">
        <DetailRow icon={Truck} label="Fahrzeug" value={booking.vehicle_name} />
        <DetailRow
          icon={Calendar}
          label="Zeitraum"
          value={`${formatDate(booking.start_date)} – ${formatDate(booking.end_date)}`}
        />
        <DetailRow icon={Clock} label="Miettage" value={`${booking.total_days} Tage`} />
        <div className="border-t border-[#1e3a5f] pt-4">
          <div className="flex justify-between items-baseline">
            <span className="text-base font-semibold text-white">Gesamtpreis</span>
            <span className="text-2xl font-bold text-[#0F1A2E]">{booking.total_price.toFixed(2)}€</span>
          </div>
        </div>
      </div>

      <div className="bg-[#0F1A2E]/5 p-6 border-l-4 border-[#0F1A2E] mb-8">
        <p className="text-sm text-white/70">
          <span className="font-semibold text-white">Buchungsnummer:</span>{' '}
          {booking.id.substring(0, 8).toUpperCase()}
        </p>
        <p className="text-sm text-white/70 mt-1">
          Eine Bestätigung wurde an <span className="font-medium">{booking.customer_email}</span> gesendet.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <Link
          to="/vermietung"
          data-testid="back-to-rental"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0F1A2E] text-white px-6 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#F5A623] transition-colors"
        >
          Weitere Fahrzeuge
          <ArrowRight size={16} />
        </Link>
        <Link
          to="/"
          className="flex-1 inline-flex items-center justify-center gap-2 border border-[#0F1A2E] text-[#0F1A2E] px-6 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#0F1A2E] hover:text-white transition-colors"
        >
          Zur Startseite
        </Link>
      </div>
    </motion.div>
  );
}

function FailedState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A1220] p-8 md:p-12 border border-[#1e3a5f] text-center"
    >
      <div className="w-16 h-16 bg-red-100 flex items-center justify-center mx-auto mb-6">
        <XCircle size={32} className="text-red-600" />
      </div>
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
        Zahlung fehlgeschlagen
      </h2>
      <p className="text-white/70 mb-8">
        Die Zahlungssitzung ist abgelaufen oder wurde abgebrochen.
      </p>
      <Link
        to="/vermietung"
        data-testid="retry-rental"
        className="inline-block bg-[#0F1A2E] text-white px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#F5A623] transition-colors"
      >
        Erneut versuchen
      </Link>
    </motion.div>
  );
}

function ErrorState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A1220] p-8 md:p-12 border border-[#1e3a5f] text-center"
    >
      <div className="w-16 h-16 bg-yellow-100 flex items-center justify-center mx-auto mb-6">
        <XCircle size={32} className="text-yellow-600" />
      </div>
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
        Ein Fehler ist aufgetreten
      </h2>
      <p className="text-white/70 mb-8">
        Der Zahlungsstatus konnte nicht abgefragt werden. Bitte kontaktieren Sie uns.
      </p>
      <Link
        to="/kontakt"
        className="inline-block bg-[#0F1A2E] text-white px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#F5A623] transition-colors"
      >
        Kontakt aufnehmen
      </Link>
    </motion.div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-3 text-white/70">
        <Icon size={18} className="text-[#F5A623]" />
        <span className="text-sm">{label}</span>
      </div>
      <span className="text-sm font-medium text-white">{value}</span>
    </div>
  );
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
